/** @jsx h */
import { $ } from 'utils/dom';
import {
  labels,
  endpoint,
  showMaininput,
  autoSuggest,
  geoCoordinates,
  searchType,
  showFacets,
  restrictionFilters,
} from '../config';
import { Fragment } from 'preact';
// import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { useState, useRef, useEffect } from 'preact/hooks';
import { fetchSearchData, replaceUrl } from '../helpers/helpers';
import axios from 'axios';
import { searchStore } from '../store/store';
import SearchHeaderMap from '../components/SearchHeader/SearchHeaderMap';
import { Map } from '../components/Map/Map';
import MapAlternative from '../components/Map/MapAlternative';
import Loading from '../components/Loading';
import MapAutosuggest from '../helpers/MapAutoSuggest';

/* ****
*** NOTE:
There are 2 variants of the map/location search, that both use the same endpoint:
1. map-zip: for entering zip-codes only to ensure that the location to be contacted can be determined and highlighted. Before submit the query is checked if it's a valid zip-code. No photon autosuggest (since that doesn't work for zip-codes).
2. map-zip-city: for entering zips and/or location. With photon autosuggest. After submit the input is checked, whether the string contains a valid zip-code. If yes: check for location / centers zu be highlighted.
*** */

const SearchMapZipCity = ({ initialSearchData, pluginNamespace }) => {
  // set default states
  const [searchData, setSearchData] = useState(initialSearchData);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [resultIsEmpty, setResultIsEmpty] = useState(
    searchData.allResultCount === 0
  );
  const [inputQuery, setInputQuery] = useState(searchData.arguments);
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [noCoordinatesFound, setNoCoordinatesFound] = useState(false);
  const [resultList, setResultList] = useState(
    searchData.documents.list.results
  );
  const [resultHighlight, setResultHighlight] = useState(null);
  const [autoSuggestClass, setAutoSuggestClass] = useState(null);
  const formEl = useRef(null);
  const paginationInputEl = useRef(null);

  // Limit visible results on successful retrieval of location coordinates.
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // get setSearchJson function from the store
  const setSearchJson = searchStore(state => state.setSearchJson);

  function resetInput(selector) {
    formEl.current.querySelectorAll(selector).forEach(input => {
      if (input.checked) input.checked = false;
      if (input.value) input.value = '';
    });
  }

  function resetFilters(keys = [], exclude = [], query = '') {
    const form = formEl.current;
    const formData = new FormData(form);

    if (keys?.length > 0) {
      keys.forEach(key => {
        resetInput(`[name='${key}']`);
      });

      triggerSearch(false, query);

      return;
    }

    // if it's a new query, we need to reset all previous facets
    for (const pair of formData.entries()) {
      const key = pair[0];
      const value = pair[1];
      // clear all filter except input query and restrictionFilters
      if (!exclude.includes(key) && !restrictionFilters.includes(value)) {
        resetInput(`[name='${key}']`);
      }
    }

    triggerSearch(false, query);
  }

  async function retrieveCoordinates(query) {
    if (!query) return;
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    toggleLoadingAnimation(true);

    // submit request to geocoding server and get lat and lon and update state accordingly
    const response = await axios.get(
      `${geoCoordinates}?q=${query}&limit=1&countrycodes=de`,
      config
    );

    toggleLoadingAnimation(false);

    if (response.data.length) {
      const lat = response.data[0].lat;
      const lon = response.data[0].lon;
      const coords = {
        latLonStr: `${lat},${lon}`,
        lat,
        lon,
      };
      setLat(lat);
      setLon(lon);
      setNoCoordinatesFound(false);
      return coords;
    } else {
      // if no coordinates were found for the query string: render error message
      setNoCoordinatesFound(true);
      setLat(null);
      setLon(null);
      return null;
    }
  }

  function getZipFromQuery(query) {
    // check with regex if search string contains a zip-code and return the match
    const zipRegex = /^(?!01000|99999)(0[1-9]\d{3}|[1-9]\d{4})$/;
    const match = query
      .replaceAll(',', '')
      .split(' ')
      .filter(word => zipRegex.test(word));
    if (match.length) return match[0];
  }

  function handleSubmit(e) {
    e && e.preventDefault();
    triggerSearch();
  }

  function handleLoadMore() {
    triggerSearch(true);
  }

  const getAddressQuery = () => {
    const formData = new FormData(formEl.current);
    return formData.get(`${pluginNamespace}[q]`);
  };

  async function triggerSearch(isLoadMore = false, query = '') {
    // cancel on-going autosuggest call
    if (autoSuggestClass) {
      autoSuggestClass.cancelAjax(true);
    }

    const formData = new FormData(formEl.current);

    // update additionalFilters
    const location = query || getAddressQuery();
    let coords = '';
    if (location !== '') {
      coords = await retrieveCoordinates(location);
    } else {
      // if a user empties the address input field: reset hiddentInput's value, coordinates and range value
      setNoCoordinatesFound(false);
      setLat(null);
      setLon(null);
    }

    // update query, if one was passed into this function
    if (query) formData.set(`${pluginNamespace}[q]`, query);
    const formQuery = formData.get(`${pluginNamespace}[q]`);

    // reset filters if user is searching with a new query
    // if (formQuery && inputQuery) {
    //   const isNewQuery = formQuery !== inputQuery;
    //   if (isNewQuery) resetFilters(formData);
    // } else if ((formQuery && !inputQuery) || (!formQuery && inputQuery)) {
    //   resetFilters(formData);
    // }

    setInputQuery(formQuery);

    formData.delete(`${pluginNamespace}[q]`);

    // Set payload [q] if there are location coordinates
    const coordsQuery = searchData.facets.filter(
      facet => facet.name === 'periphery'
    )[0].options?.q;
    if (coords && coordsQuery) {
      formData.set(`${pluginNamespace}[q]`, coordsQuery);
    }

    if (formData.get(`${pluginNamespace}[sort]`) === '') {
      formData.delete(`${pluginNamespace}[sort]`);
    }
    if (!isLoadMore && formData.get(`${pluginNamespace}[page]`)) {
      formData.delete(`${pluginNamespace}[page]`);
    }
    if (
      formData.get(`${pluginNamespace}[additionalFilters][]`) === '' ||
      coords === ''
    ) {
      formData.delete(`${pluginNamespace}[additionalFilters][]`);
    }
    if (coords) {
      formData.set(`${pluginNamespace}[lat]`, coords.lat);
      formData.set(`${pluginNamespace}[long]`, coords.lon);
    }

    getSearchData(formData, isLoadMore, formQuery);
  }

  function getSearchData(formData, isLoadMore = false, query = '') {
    setIsLoading(true);
    setIsError(false);
    fetchSearchData(endpoint, formData)
      .then(({ data }) => {
        const resultData = data.content.colPos0[0].content.data;
        const hasNoResults = resultData.allResultCount === 0;
        const resultDataList = resultData.documents.list.results;

        // if there are no results, show no-result text
        if (hasNoResults) {
          setResultIsEmpty(true);
          setResultList([]);
          setSearchJson(resultData);
          setSearchData(resultData);
        } else {
          setResultIsEmpty(false);
          setSearchData(resultData);

          if (isLoadMore) {
            // append next item into array, when is loadmore (cause we dont want to replace the prev items)
            setResultList([...resultList, ...resultDataList]);

            /*
             * onLoadMore, we dont want to replace the whole searchJson (from the store)
             * We only need to update the pagination number and the result count
             * and append the new incoming result list to the old one
             */
            const searchJson = searchStore.getState().searchJson;
            const searchJsonCount = searchJson.documents.list.count;

            // append new result to the old result
            searchJson.documents.list.results = [
              ...resultList,
              ...resultDataList,
            ];

            // update searchJson count
            searchJson.documents.list.count =
              searchJsonCount + resultData.documents.list.count;

            // update current pagination
            searchJson.pagination.current = resultData.pagination.current;

            // save it in our searchStore (needed for popstate later)
            setSearchJson(searchJson);
          } else {
            // replace the whole list
            const searchJson = searchStore.getState().searchJson;

            const zip = getZipFromQuery(query);
            // split the resultDataList:
            // --- filter out the result whose 'zip' property contains the query string (postcode) => highlight (array)
            // --- keep the rest (all results excluding the highlighted one) => list (array)
            const isHighlight = result =>
              (result.zips || result.zps)?.includes(zip);
            const splitResultArray = (array, isHighlight) => {
              return array.reduce(
                ([highlight, list], item) => {
                  return isHighlight(item)
                    ? [[...highlight, item], list]
                    : [highlight, [...list, item]];
                },
                [[], []]
              );
            };
            const [highlight, list] = splitResultArray(
              resultDataList,
              isHighlight
            );
            const highlistItems = highlight.map(item => ({
              ...item,
              isHighlight: true,
            }));

            // add highlight item to searchJson
            searchJson.documents.list.highlight = highlistItems;
            // override results in searchJson
            searchJson.documents.list.results = list;

            setResultHighlight(highlistItems);
            setResultList(list);

            // save response json in our searchStore (needed for popstate later)
            setSearchJson(searchJson);
          }
        }

        // add search params to the url
        replaceUrl(
          process.env.NODE_ENV === 'production'
            ? endpoint
            : '/api/v1/locations',
          resultData.currentSearch,
          formData,
          pluginNamespace
        );

        setIsLoading(false);
        setIsReset(false);
      })
      .catch(error => {
        console.error('Search Error:', error);
        setIsLoading(false);
        setIsError(true);
        setIsReset(false);
      });
  }

  function toggleLoadingAnimation(enable) {
    setIsLoading(enable);
  }

  function getRestrictionFiltersAsObj() {
    const obj = {};

    restrictionFilters &&
      restrictionFilters.forEach(filter => {
        const splitArray = filter.split(':');
        const key = splitArray[0];
        const value = splitArray[1];

        if (key && value) obj[key] = value;
      });

    return obj;
  }

  // init autosuggest
  useEffect(() => {
    if (autoSuggest) {
      const form = formEl.current;
      const searchInput = $('[data-main-input]', form);

      if (form && searchInput) {
        const autoSuggestObj = new MapAutosuggest({
          // input which triggers solr
          input: searchInput,
          // form element which contains the input
          form,
          // [optional] min length of text until we fire the ajax-request (0 is default)
          textMinLength: 2,
          // [optional] set true, if you want to enable typeahead
          typeahead: false,
          // [optional] prevent AutoSuggest from submitting the form (e.g. if you want to trigger ajax instead) (default is false)
          preventSubmit: true,
          // [optional] allow empty submit even if input is empty (default is false)
          allowEmptySubmit: true,
          // [optional] submit form if user clicks on a suggestion button (default is false)
          submitOnSuggestionClick: false,
          // [optional] additional geo service parameters
          additionalParams: `&limit=7&bbox=5.98865807458,47.3024876979,15.0169958839,54.983104153&layer=city&layer=district`, // 'limit' amount of suggestions, give boundary-box ('bbox') of Germany, only suggest cities ('city'), towns and districts ('district')
          onSelecting: query => {
            resetFilters(false, ['tx_solr[q]'], query);
          },
        });
        autoSuggestObj.init();

        setAutoSuggestClass(autoSuggestObj);
      }
    }
  }, []);

  return (
    <Fragment>
      {/* show loading animation, while fetching */}
      {isLoading && <Loading />}
      <form
        className="search-form"
        action="#"
        onSubmit={handleSubmit}
        ref={formEl}
        data-suggest={autoSuggest}
        aria-labelledby="search-form"
      >
        <h2 id="search-form" className="sr-only">
          {labels.map.search}
        </h2>
        {restrictionFilters.length
          ? restrictionFilters.map((restrictionFilter, key) => (
              <input
                key={key}
                id={`${restrictionFilter}`}
                className="is-hidden"
                type="checkbox"
                name={`${pluginNamespace}[filter][]`}
                value={`${restrictionFilter}`}
                data-label={``}
                checked="checked"
              />
            ))
          : ''}
        {searchData.facets.length > 0 || showMaininput ? (
          <SearchHeaderMap
            showFacets={showFacets}
            query={inputQuery}
            facets={searchData.facets}
            facetsActive={searchData.facetsActive}
            sorting={searchData.sorting}
            triggerAjaxSubmit={handleSubmit}
            pluginNamespace={pluginNamespace}
            searchType={searchType}
            showMaininput={showMaininput}
            resetFilters={resetFilters}
            isReset={isReset}
            toggleLoadingAnimation={toggleLoadingAnimation}
            coordinatesApi={geoCoordinates}
            noCoordinatesFound={noCoordinatesFound}
          />
        ) : (
          ''
        )}
        <input
          type="text"
          name={`${pluginNamespace}[page]`}
          hidden
          readOnly
          ref={paginationInputEl}
        />
      </form>
      <div className="search-contacts">
        <div className="search-contacts__map">
          <Map
            highlight={resultHighlight}
            list={resultList}
            consent={true}
            lat={lat}
            lon={lon}
            isLoading={isLoading}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
          />
        </div>
        <div className="search-contacts__map-alternative">
          <MapAlternative
            searchType={searchType}
            allResultCount={searchData.allResultCount}
            highlight={resultHighlight}
            list={resultList}
            listCount={searchData.documents.list.count}
            pagination={searchData.pagination}
            loadMore={handleLoadMore}
            pluginNamespace={pluginNamespace}
            isError={isError}
            resultIsEmpty={resultIsEmpty}
            paginationInputEl={paginationInputEl}
            restrictionFilters={getRestrictionFiltersAsObj()}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default SearchMapZipCity;
